// Game data on the public event shape (D-368).
//
// `GET /api/public/events` and `GET /api/public/events/:slug` carry these on
// every event. The storefront's schedule codes against the names exactly, so
// they live here rather than only in the API: the API's own tests parse its
// responses with these schemas, and the storefront can import the types.
import { z } from 'zod';

/** The team a home game is against. `null` on the event means it is not a game. */
export const PublicEventOpponent = z.object({
  id: z.string(),
  /** What `GET /api/public/events?opponent=` takes (D-375). */
  slug: z.string(),
  name: z.string(),
  short_name: z.string().nullable(),
  abbreviation: z.string().nullable(),
  /** Relative to the API origin, like `artwork_url`. */
  logo_url: z.string().nullable(),
});
export type PublicEventOpponent = z.infer<typeof PublicEventOpponent>;

/** The game's promotion. `null` on the event when it has no promotion title. */
export const PublicEventPromo = z.object({
  title: z.string().min(1),
  detail: z.string().nullable(),
  sponsor: z.string().nullable(),
});
export type PublicEventPromo = z.infer<typeof PublicEventPromo>;

/**
 * One offer a promotion carries (D-377): what the fan gets. `badge` is the
 * short text the schedule prints ("2 FOR 1", "$1 HOT DOGS"), null for an
 * informational line with no label of its own.
 */
export const PublicPromotionOffer = z.object({
  kind: z.enum(['INFO', 'TICKET_DEAL', 'CONCESSION_SPECIAL', 'GIVEAWAY']),
  badge: z.string().nullable(),
  detail: z.string().nullable(),
  /** "First 1,000 fans"; null on anything that is not a giveaway with a count. */
  giveaway_quantity: z.number().int().nullable(),
});
export type PublicPromotionOffer = z.infer<typeof PublicPromotionOffer>;

/** Where a promotion is filed on the schedule: its chip. */
export const PublicPromotionChip = z.object({ code: z.string(), name: z.string() });
export type PublicPromotionChip = z.infer<typeof PublicPromotionChip>;

/**
 * A library promotion as it stands on one game (D-377): the per-game sponsor
 * and detail already applied. `promo` on the event is the featured one of
 * these in D-368's shape, kept for compatibility.
 */
export const PublicEventPromotion = z.object({
  slug: z.string(),
  name: z.string(),
  headline: z.string(),
  detail: z.string().nullable(),
  sponsor: z.string().nullable(),
  icon: z.string(),
  image_url: z.string().nullable(),
  accent_color: z.string().nullable(),
  chip: PublicPromotionChip,
  featured: z.boolean(),
  /** An automatic ticket deal applies at checkout on this game. */
  has_ticket_deal: z.boolean(),
  offers: z.array(PublicPromotionOffer),
});
export type PublicEventPromotion = z.infer<typeof PublicEventPromotion>;

/** The fields every public event gains. */
export const PublicEventGameFields = z.object({
  opponent: PublicEventOpponent.nullable(),
  promo: PublicEventPromo.nullable(),
  promotions: z.array(PublicEventPromotion),
});
export type PublicEventGameFields = z.infer<typeof PublicEventGameFields>;

/**
 * What kind of game it is (D-374), for the schedule's labels: a playoff game
 * ("Playoffs"), one played only if the series needs it ("If necessary", its
 * tickets refunded automatically if not), and a single-admission doubleheader.
 * Every public event carries all three; each is false on anything that is not
 * a game.
 */
export const PublicEventGameFlags = z.object({
  playoff: z.boolean(),
  if_necessary: z.boolean(),
  doubleheader: z.boolean(),
});
export type PublicEventGameFlags = z.infer<typeof PublicEventGameFlags>;

/**
 * The series an event is part of (D-324), and since D-375 what a baseball
 * series adds: the team its games are against and its dates, which are its
 * games' dates (nothing is stored). `games` counts its published games, played
 * or to come; `has_packages` is whether a package sold with it is on sale.
 */
export const PublicEventSeries = z.object({
  slug: z.string(),
  name: z.string(),
  opponent: PublicEventOpponent.nullable(),
  starts_on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  ends_on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  games: z.number().int().min(0),
  has_packages: z.boolean(),
});
export type PublicEventSeries = z.infer<typeof PublicEventSeries>;

/** One choice in the schedule's Opponent dropdown: a team with a game still to come (D-375). */
export const PublicScheduleOpponent = z.object({
  slug: z.string(),
  name: z.string(),
  short_name: z.string().nullable(),
  logo_url: z.string().nullable(),
});
export type PublicScheduleOpponent = z.infer<typeof PublicScheduleOpponent>;

/** A game a promotion is on, for its card on `/promotions` (D-377). */
export const PublicPromotionGame = z.object({
  slug: z.string(),
  title: z.string(),
  starts_at: z.string(),
  timezone: z.string(),
  opponent: PublicEventOpponent.nullable(),
});
export type PublicPromotionGame = z.infer<typeof PublicPromotionGame>;

/**
 * One card on the storefront's `/promotions` (D-377): the promotion as the
 * library holds it, "Every Monday"-style text worked out from its rules, and
 * its next games.
 */
export const PublicPromotionCard = z.object({
  slug: z.string(),
  name: z.string(),
  headline: z.string(),
  detail: z.string().nullable(),
  sponsor: z.string().nullable(),
  icon: z.string(),
  image_url: z.string().nullable(),
  accent_color: z.string().nullable(),
  chip: PublicPromotionChip,
  schedule_text: z.string(),
  has_ticket_deal: z.boolean(),
  offers: z.array(PublicPromotionOffer),
  upcoming_games: z.number().int().min(0),
  next_games: z.array(PublicPromotionGame),
});
export type PublicPromotionCard = z.infer<typeof PublicPromotionCard>;

/** One choice in the schedule's promotion chips: a promotion with a game still to come (D-377). */
export const PublicSchedulePromotion = z.object({
  slug: z.string(),
  name: z.string(),
  icon: z.string(),
  chip: PublicPromotionChip,
});
export type PublicSchedulePromotion = z.infer<typeof PublicSchedulePromotion>;
